import { Eye, Loader2, Plus } from "lucide-react";
import { PROFESSION_CATEGORY_LABELS } from "../data/professionalTaxonomy";
import { type ProfessionalMemberCredential, type ProfessionalMemberRequest, type ProfessionalRequestSummary } from "../lib/api";
import { STATUS_STYLE } from "./professionalVerificationModel";

export function RequestCard({
  request,
  expanded,
  detail,
  loadingDetail,
  detailError,
  evidenceLoadingId,
  onToggle,
  onCorrect,
  onAddCredential,
  onViewEvidence,
  preview,
}: {
  request: ProfessionalRequestSummary;
  expanded: boolean;
  detail: ProfessionalMemberRequest | null;
  loadingDetail: boolean;
  detailError: string | null;
  evidenceLoadingId: string | null;
  onToggle: () => void;
  onCorrect: () => void;
  onAddCredential: () => void;
  onViewEvidence: (imageId: string, title: string) => void;
  preview: boolean;
}) {
  return (
    <article className="rounded-lg border border-line p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[12rem]">
          <div className="text-sm font-semibold text-navy">{request.professionTitle}</div>
          <div className="text-xs text-mut">{request.credentialCount} credential{request.credentialCount === 1 ? "" : "s"} · updated {new Date(request.updatedAt).toLocaleDateString()}</div>
        </div>
        <span className={`chip capitalize ${STATUS_STYLE[request.status]}`}>{request.status}</span>
        <button type="button" onClick={onToggle} className="btn-outline h-8 px-3 text-xs">{expanded ? "Hide details" : "View details"}</button>
        {request.status === "rejected" && !preview && <button type="button" onClick={onCorrect} className="btn-outline h-8 px-3 text-xs">Correct and resubmit</button>}
        {request.status === "approved" && !preview && <button type="button" onClick={onAddCredential} className="btn-outline h-8 px-3 text-xs"><Plus className="w-3.5 h-3.5" />Add credential</button>}
      </div>
      {request.decisionNote && <p className="mt-2 text-sm text-brand-red">Review note: {request.decisionNote}</p>}
      {expanded && (
        <div className="mt-3 border-t border-line pt-3">
          {loadingDetail && <p className="text-sm text-mut">Loading request evidence…</p>}
          {detailError && <p role="alert" className="text-sm text-brand-red">{detailError}</p>}
          {detail && <div className="space-y-3">{detail.credentials.map((credential) => (
            <CredentialSummary key={credential.id} requestId={detail.id} credential={credential} evidenceLoadingId={evidenceLoadingId} onViewEvidence={onViewEvidence} />
          ))}</div>}
        </div>
      )}
    </article>
  );
}

function CredentialSummary({
  requestId,
  credential,
  evidenceLoadingId,
  onViewEvidence,
}: {
  requestId: string;
  credential: ProfessionalMemberCredential;
  evidenceLoadingId: string | null;
  onViewEvidence: (imageId: string, title: string) => void;
}) {
  return (
    <div className="rounded-md bg-navy-50/60 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[10rem]">
          <div className="text-sm font-semibold text-navy">{credential.credentialType}{credential.credentialNumber ? ` · ${credential.credentialNumber}` : ""}</div>
          <div className="text-xs text-mut">{credential.issuer}{credential.expiryDate ? ` · Expires ${credential.expiryDate}` : ""}</div>
          <div className="text-xs text-mut">{credential.qualificationPath ? PROFESSION_CATEGORY_LABELS[credential.qualificationPath] : "Qualification path not recorded"}</div>
        </div>
        <span className={`chip capitalize ${credential.status === "approved" ? STATUS_STYLE.approved : credential.status === "rejected" ? STATUS_STYLE.rejected : STATUS_STYLE.pending}`}>{credential.status}</span>
      </div>
      {credential.decisionNote && <p className="text-xs text-brand-red mt-1">Review note: {credential.decisionNote}</p>}
      <div className="mt-2 flex flex-wrap gap-2">
        {credential.images.map((image, index) => (
          <button key={image.id} type="button" onClick={() => onViewEvidence(image.id, `${credential.credentialType} evidence ${index + 1}`)} disabled={evidenceLoadingId === image.id} className="btn-ghost h-8 px-2 text-xs">
            {evidenceLoadingId === image.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
            Evidence {index + 1} · {image.contentType.split("/")[1].toUpperCase()}
          </button>
        ))}
        {credential.images.length === 0 && <span className="text-xs text-mut">No evidence images attached.</span>}
      </div>
      <span className="sr-only">Request {requestId}</span>
    </div>
  );
}
